import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { TPersonalInformation } from '../../components/PqDataEntryForm/providers/PersonalInformationProvider';
import { THomeAddress } from '../../components/PqDataEntryForm/providers/HomeAddressProvider';
export interface IPqDataEntryFormProps extends MappedComponentProperties {
    /**
     * In Author Dialogue: GENERAL > Version
     */
    version: 'versionA' | 'versionB' | 'orangeAsteriskVariant' | 'singleAddressVariant' | 'invalidAddressPopUpVariant' | 'addressLockVariant';
    /**
     * For testing purposes (not an authorable field).
     */
    id: string;
    /**
     * In Author Dialogue: GENERAL > Headline
     */
    headlines: { text: string | null }[];
    /**
     * In Author Dialogue: GENERAL > Redirect Url (Full Application Page)
     */
    fullAppRedirectUrl: string;
    /**
     * In Author Dialogue: GENERAL > Redirect Url (Multiple Offer Page)
     */
    multiOfferRedirectUrl: string;
    /**
     * In Author Dialogue: GENERAL > Redirect Url (PQ Home Page)
     */
    pqHomeRedirectUrl: string;
    /**
     * In Author Dialogue: GENERAL > Redirect Url (E-Sign Page)
     */
    esignConsentRedirectUrl: string;
    /**
     * In Author Dialogue: GENERAL > Redirect Url (Counter Offer E-sign Page)
     */
    counterOfferEsignConsentRedirectUrl: string;
    /**
     * In Author Dialogue: GENERAL > Required Fields
     */
    emailRequired: boolean;
    /**
     * In Author Dialogue: GENERAL > Hide Fields
     */
    phoneNumberHidden: boolean;
    /**
     * In Author Dialogue: GENERAL > Hide Fields
     */
    authorableCtaButtonUsed: boolean;
    /**
     * This field is actually not used in production
     *
     * In Author Dialogue: CARD BENEFIT INFO > Text for below the Card Image
     */
    cardImageText: string;
    /**
     * **This is an RTE Field**
     *
     * In Author Dialogue: FORM > Sign In Question Text Field
     */
    signInQuestionText: string;
    /**
     * In Author Dialogue: FORM > Sign In Question URL for Form Intro text
     */
    signInQuestionUrl: string;
    /**
     * In Author Dialogue: FORM > Form Title
     */
    formTitle: string;
    /**
     * **This is an RTE Field**
     *
     * In Author Dialogue: FORM > Form Subtitle
     */
    formSubtitle: string;
    /**
     * In Author Dialogue: FORM > Disclaimer for WI
     */
    wiResidentDisclaimerText: string;
    /**
     * for Patriot Act 326
     *
     * In Author Dialogue: FORM > Mailing Address Note
     */
    mailingAddressNote: string;
    /**
     * In Author Dialogue: FORM > Email Input Message
     */
    emailMessage: string;
    /**
     * In Author Dialogue: FORM > Social Security Number Input Message
     */
    ssnMessage: string;
    /**
     * In Author Dialogue: FORM > Check Apt Unit Po Box
     */
    aptUnitPoBox: boolean;
    /**
     * In Author Dialogue: FORM > Re-enter Address CTA Text
     */
    reEnterAddressCtaText?: string;
    /**
     * **This is an RTE Field**
     *
     * In Author Dialogue: MISCELLANEOUS > Disclaimer for Desktop
     */
    disclaimerDesktop?: string;
    /**
     * In Author Dialogue: MISCELLANEOUS > Secondary Disclaimer for Desktop
     */
    disclaimerDesktopSecondary?: string;
    /**
     * **This is an RTE Field**
     *
     * In Author Dialogue: MISCELLANEOUS > Disclaimer for Mobile
     */
    disclaimerMobile?: string;
    /**
     * In Author Dialogue: MISCELLANEOUS > Secondary Disclaimer for Mobile
     */
    disclaimerMobileSecondary?: string;
    /**
     * In Author Dialogue: MISCELLANEOUS > Learn More Link
     */
    learnMoreLink?: string;
    /**
     * In Author Dialogue: MISCELLANEOUS > Learn More Link Text
     */
    learnMoreLinkText?: string;
    /**
     * In Author Dialogue: MISCELLANEOUS > Terms & Conditions Text
     */
    tncLinkText: string;
    /**
     * In Author Dialogue: MISCELLANEOUS > Terms & Conditions > Open in New Tab (checkbox)
     */
    tncTarget?: '_blank';
    /**
     * In Author Dialogue: PUSHDOWN MESSAGES > Pushdown Message Title
     */
    errorTitle?: string;
    /**
     * In Author Dialogue: PUSHDOWN MESSAGES > Pushdown Message Description
     */
    errorDesc?: string;
    /**
     * In Author Dialogue: PUSHDOWN MESSAGES > Multiple Account Offer Error Code
     */
    multiAcctErrorCode?: string;
    /**
     * In Author Dialogue: PUSHDOWN MESSAGES > API Response NULL Error Code
     */
    apiResponseErrorCode?: string;
    /**
     * In Author Dialogue: EMAIL AUTO FILL > Email Auto Fill
     */
    emailAutoFill?: {
        /**
         * In Author Dialogue: EMAIL AUTO FILL > Email Auto Fill > Autofill Domain
         */
        domain: string | null;
    }[];
    pqMappingContentPath?: string;
    cqPath: string;
    cqType: string;
    isInEditor: boolean;
    author: boolean;
    suggestedAddressEnabled?: boolean;
    suggestedAddressHeadline?: string;
    suggestedAddressSubHeadline?: string;
    suggestedAddressAcceptCtaText?: string;
    suggestedAddressDeclineCtaText?: string;
    avcThreshold?: string;
    cityStateRetrievalEnabled?: boolean;
    invalidAddressHeadline?: string;
}

export const namesAndIds: Record<
    | keyof TPersonalInformation
    | keyof THomeAddress,
    { name: string; id: string; label: string; pushDownMessage: string }
> = {
    FirstName: {
        name: 'firstName',
        id: 'firstNamePQDE',
        label: 'First Name',
        pushDownMessage: 'First Name',
    },
    MiddleInitial: {
        name: 'middleInitial',
        id: 'middleInitialPQDE',
        label: 'Middle Initial',
        pushDownMessage: 'Middle Initial',
    },
    LastName: {
        name: 'lastName',
        id: 'lastNamePQDE',
        label: 'Last Name',
        pushDownMessage: 'Last Name',
    },
    MailingAddressLine1: {
        name: 'mailingAddress',
        id: 'mailingAddressPQDE',
        label: 'Mailing Address',
        pushDownMessage: 'Mailing Address',
    },
    MailingAddressLine2: {
        name: 'aptUnit',
        id: 'aptUnitPQDE',
        label: 'Apt/Unit #',
        pushDownMessage: 'Apt/Unit #',
    },
    MailingCity: {
        name: 'city',
        id: 'cityPQDE',
        label: 'City',
        pushDownMessage: 'Mailing Address City',
    },
    MailingState: {
        name: 'state',
        id: 'statePQDE',
        label: 'State',
        pushDownMessage: 'Mailing Address State',
    },
    MailingZipCode: {
        name: 'zipCode',
        id: 'zipCodePQDE',
        label: 'ZIP Code (XXXXX)',
        pushDownMessage: 'Mailing Address ZIP Code',
    },
    /**
     * required set through props
     */
    EmailAddress: {
        name: 'email',
        id: 'emailPQDE',
        label: 'Email Address (email@domain.com)',
        pushDownMessage: 'Email Address (email@domain.com)',
    },
    Ssn: {
        name: 'socialSecurity',
        id: 'socialSecurityPQDE',
        label: 'Social Security Number (XXX-XX-XXXX)',
        pushDownMessage: 'Social Security Number',
    },
    dob: {
        name: 'dateOfBirth',
        id: 'dateOfBirthPQDE',
        label: 'Date of Birth (MM-DD-YYYY)',
        pushDownMessage: 'Date of Birth (MM-DD-YYYY)',
    },
    PrimaryPhoneNumber: {
        name: 'phoneNumber',
        id: 'phoneNumberPQDE',
        label: 'Phone Number (XXX) XXX-XXXX',
        pushDownMessage: 'Phone Number (XXX) XXX-XXXX',
    },
    /**
     * required set through props
     */
    HomeAddressLine1: {
        name: 'homeAddressMailingAddress',
        id: 'homeAddress-mailingAddressPQDE',
        label: 'Home Street Address',
        pushDownMessage: 'Home Street Address',
    },
    HomeAddressLine2: {
        name: 'homeAddressAptUnit',
        id: 'homeAddress-aptUnitPQDE',
        label: 'Apt/Unit #',
        pushDownMessage: 'Apt/Unit #',
    },
    HomeCity: {
        name: 'homeAddressCity',
        id: 'homeAddress-cityPQDE',
        label: 'City',
        pushDownMessage: 'Home Address City',
    },
    HomeState: {
        name: 'homeAddressState',
        id: 'homeAddress-statePQDE',
        label: 'State',
        pushDownMessage: 'Home Address State',
    },
    HomeZipCode: {
        name: 'homeAddressZipCode',
        id: 'homeAddress-zipCodePQDE',
        label: 'ZIP Code (XXXXX)',
        pushDownMessage: 'Home Address ZIP Code',
    },
};

/**
 * Content Fragment type is **PQMappingContent**
 */
export interface IPQMappingContent {
    /**
     * In Author: Mapping Content > Bullets & Icons
     */
    bulletsAndIcons?: {
        /**
         * In Author: Mapping Content > Bullets & Icons > Icon
         */
        keyValues1_iconMF?: {
            iconLabel: string;
            iconAltText: string;
            iconPath: string;
            uuid: string;
        };
        /**
         * In Author: Mapping Content > Bullets & Icons > Bullet Title
         */
        keyValues1_bulletTitle?: string;
        /**
         * In Author: Mapping Content > Bullets & Icons > Bullet Text
         */
        keyValues1_bulletText?: string;
    }[];
    /**
     * In Author: Mapping Content > Card Art - Alt Text
     */
    cardArtAltTextMF?: string;
    /**
     * In Author: Mapping Content > Card Art Image Path
     */
    cardArtImagePathMF?: string;
    /**
     * In Author: Mapping Content > Descriptor Id
     */
    discriptorId?: string;
    /**
     * **This is treated like an RTE Field**
     *
     * In Author: Mapping Content > Disclaimer Text
     */
    disclaimerText?: string;
    /**
     * In Author: Mapping Content > Enable Mobile Bullet Points (checkbox)
     */
    enableMBPointsMF: 'true' | 'false';
    /**
     * In Author: Mapping Content > Experience Fragment
     */
    experienceFragment?: string;
    /**
     * In Author: Mapping Content > Mobile Bullet Points
     */
    mobileBulletPoints?: {
        /**
         * In Author: Mapping Content > Mobile Bullet Points > Bullet Text
         */
        keyValues0_bulletTextMB?: string;
        /**
         * In Author: Mapping Content > Mobile Bullet Points > Icon
         */
        keyValues0_iconMB?: {
            iconLabel: string;
            iconAltText: string;
            iconPath: string;
            uuid: string;
        };
    }[];
    /**
     * In Author: Mapping Content > Partner ID
     */
    partnerId?: string;
    /**
     * as of 4-24-23, in the author instance, it does not send the text written in the corresponding field, and only send the first in the list of program codes
     *
     * In Author: Mapping Content > Program Codes
     */
    programCode?: string;
    /**
     * In Author: Mapping Content > Ribbon Text
     */
    ribbonText?: string;
    /**
     * In Author: Mapping Content >
     */
    selectTermsConditionsLinkType?:
    | 'Experience Fragment'
    | 'Static Terms & Conditions Link';
    /**
     * In Author: Mapping Content > Source ID*
     */
    sourceId?: string;
    /**
     * In Author: Mapping Content > Static Terms & Conditions Link
     */
    staticTermsConditionsLink?: string;
    /**
     * In Author: Mapping Content > Text for below the Card Image
     */
    textForBelowTheCardImageMF?: string;

    enablePremiumCardDisclosure?: true | false;
    premiumCardDisclosure?: string;
    disableTermsAndConditions?: boolean;
}

export enum EPrequalDecisionResult {
    APPROVED = 0,
    DECLINED_WITH_E_SIGN_CREDIT = 1,
    DECLINED_WITH_E_SIGN_CREDIT_NO_SCORE = 2,
    DECLINED_WITH_E_SIGN_NON_CREDIT = 3,
    DECLINED_WITH_E_SIGN_MLA = 4,
    FAILED_SUSPICIOUS_FRAUD = 5,
    ERROR = 6,
    PREQUAL_MULTI_ACCT_APPROVED = 7,
}
export interface ISubmitPreQualificationApplicationV2Body {
    PersonalInformation: {
        FirstName: string;
        MiddleInitial: string;
        LastName: string;
        EmailAddress: string;
        MailingAddressLine1: string;
        MailingAddressLine2: string;
        MailingCity: string;
        MailingState: string;
        MailingZipCode: string;
        IsMailingHomeAddressSame: boolean;
        HomeAddressLine1: string;
        HomeAddressLine2: string;
        HomeCity: string;
        HomeState: string | null;
        HomeZipCode: string;
        /**
         * type string but should only consist of number characters
         */
        PrimaryPhoneNumber: string;
        /**
         * type string but should only consist of number characters
         */
        SecondaryPhoneNumber: string;
        /**
         * type string but should only consist of number characters
         */
        MonthlyIncome: string;
        /**
         * type string but should only consist of number characters
         */
        Ssn: string;
        DOBDay: string;
        DOBMonth: string;
        DOBYear: string;
        DateOfBirth: string;
        IsDataEntryPage: boolean;
        /**
         * both are for blackbox
         * this is for third party
         * this default to an empty string
         */
        ioBlackBox: string;
        /**
         * both are for blackbox
         * this is for first party
         * this default to an empty string
         */
        fpBB: string;
        MailingAddressSelectedText: string;
        NameSuffix: string;
    };
    AdditionalInformation:
    | {
        HasCheckDebtAccount: string;
        CreditLevel: 'Excellent' | 'Average' | 'Rebuilding';
        EmploymentStatus:
        | 'Employed'
        | 'Retired'
        | 'Self-Employed'
        | 'Student'
        | 'Unemployed'
        | 'Other';
        HousingStatus: 'Own Home' | 'Rent' | 'Other';
        HasMoneyAccount: string;
        GenericQuestionId1?: number;
        GenericAnswer1: string;
        GenericQuestionId2?: number;
        GenericAnswer2: string;
    }
    | never[];
    MarketingAttributes: { Name: string; Value: string }[];
}

export interface ISubmitResponseError {
    FieldName: string;
    ErrorMessage: string;
    ErrorType: string;
}
export interface ISubmitResponse {
    PrequalDecisionResult: number;
    ApplicationInformation: any[];
    ApplicantAddress: string | null;
    IsSecuredCardPromotion: false;
    Errors: ISubmitResponseError[] | null;
    AdditionalInformation: {
        IsBalanceTransferEligible: boolean;
        IsBalanceTransferSelected: boolean;
        IsCreditProtectionEligible: boolean;
        IsCreditProtectionSelected: boolean;
        IsAuthorizedUserEligible: boolean;
        IsAuthorizedUserSelected: boolean;
        AuthorizedUserFee: number;
        SelectedProgramPlasticCode: string | null;
        Cards: any[];
        ResponseStatusCode: number;
    };
}
